/**
 * Investor Pulse Scheduler — ad-hoc trigger for the weekly pulse email.
 *
 * Founder lands on the Command Centre, taps "Send now" to immediately
 * fire a pulse (bypasses week dedup) OR picks a custom datetime + note
 * (e.g. "Right before BraveVC call") to queue an out-of-band send.
 *
 * Backend touchpoints:
 *   POST   /api/admin/investor-pulse/send       — fire immediately
 *   POST   /api/admin/investor-pulse/schedule   — queue ad-hoc
 *   GET    /api/admin/investor-pulse/schedule   — list queue
 *   DELETE /api/admin/investor-pulse/schedule/{id}
 *   GET    /api/admin/investor-pulse/preview    — opens email HTML preview
 *
 * Worker poll interval is 30 min, so a scheduled pulse fires within the
 * next half-hour after `scheduled_for` elapses (banner on the card
 * spells this out so the operator never wonders).
 */
import React, { useEffect, useState } from 'react';
import { Send, Calendar, Loader2, CheckCircle, XCircle, Eye, Trash2, Clock, Mail } from 'lucide-react';
import { apiClient } from '../../api/client';

const fmtSchedule = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
    }) + ' UTC';
  } catch { return iso.slice(0, 16); }
};

const STATUS_STYLE = {
  pending:   { bg: '#FEF3C7', text: '#92400E', label: 'Queued', Icon: Clock },
  sent:      { bg: '#D1FAE5', text: '#065F46', label: 'Sent',   Icon: CheckCircle },
  failed:    { bg: '#FEE2E2', text: '#991B1B', label: 'Failed', Icon: XCircle },
  cancelled: { bg: '#F3F4F6', text: '#374151', label: 'Cancelled', Icon: XCircle },
};

// Friday 16:00 UTC of the current ISO week — exact "before close" anchor.
const fridayCloseISO = () => {
  const now = new Date();
  const day = now.getUTCDay(); // 0 = Sun ... 5 = Fri
  const diff = (5 - day + 7) % 7 || 0;
  const target = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + diff, 16, 0, 0
  ));
  // If we're already past Friday 16:00 this week, jump to next week.
  if (target.getTime() < now.getTime()) target.setUTCDate(target.getUTCDate() + 7);
  return target.toISOString().slice(0, 16); // local datetime-local format
};

const InvestorPulseSchedulerCard = () => {
  const [recipientCount, setRecipientCount] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [scheduledFor, setScheduledFor] = useState(fridayCloseISO());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        apiClient.request('/api/admin/investor-pulse/recipients'),
        apiClient.request('/api/admin/investor-pulse/schedule'),
      ]);
      setRecipientCount(r.count);
      setSchedules(s.schedules || []);
    } catch (e) {
      if (!(e.message || '').includes('403')) console.warn('pulse load', e);
    }
  };
  useEffect(() => { load(); }, []);

  const fireNow = async () => {
    if (!window.confirm(`Send the Investor Pulse to ${recipientCount ?? '?'} recipient(s) right now? (Bypasses weekly dedup.)`)) return;
    setBusy('now'); setMsg(null); setErr(null);
    try {
      const res = await apiClient.request('/api/admin/investor-pulse/send?force=true', { method: 'POST' });
      setMsg(`Sent to ${res.emails_sent} recipient(s).`);
      load();
    } catch (e) { setErr(e.message || 'Send failed'); }
    setBusy('');
  };

  const sendTest = async () => {
    // Default: send to the logged-in admin's own email (backend pulls from JWT).
    // Optional manual override via prompt — handy when the founder wants to
    // see the rendered HTML in a personal Gmail / Outlook test inbox.
    const override = window.prompt(
      'Send a TEST pulse to which email?\n(Leave blank to send to your own admin email.)',
      ''
    );
    if (override === null) return; // user pressed Cancel
    setBusy('test'); setMsg(null); setErr(null);
    try {
      const qs = override.trim() ? `?to=${encodeURIComponent(override.trim())}` : '';
      const res = await apiClient.request(`/api/admin/investor-pulse/test${qs}`, { method: 'POST' });
      if (res.success) {
        setMsg(`Test sent to ${res.to} · subject: ${res.subject}`);
      } else {
        setErr(res.error || 'Test send failed');
      }
    } catch (e) { setErr(e.message || 'Test send failed'); }
    setBusy('');
  };

  const queue = async () => {
    if (!scheduledFor) return setErr('Pick a date + time first.');
    setBusy('queue'); setMsg(null); setErr(null);
    try {
      // Treat the datetime-local value as UTC so what the founder picks
      // matches the email-send time stamped in the worker logs.
      const iso = `${scheduledFor}:00+00:00`;
      const res = await apiClient.request('/api/admin/investor-pulse/schedule', {
        method: 'POST',
        body: JSON.stringify({ scheduled_for: iso, note }),
      });
      setMsg(`Queued for ${fmtSchedule(res.schedule.scheduled_for)}.`);
      setNote('');
      load();
    } catch (e) { setErr(e.message || 'Queue failed'); }
    setBusy('');
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this scheduled pulse?')) return;
    try {
      await apiClient.request(`/api/admin/investor-pulse/schedule/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { setErr(e.message || 'Cancel failed'); }
  };

  if (recipientCount === null) return null; // hidden for non-admin (403)

  return (
    <div className="bg-white rounded-lg shadow-sm p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="investor-pulse-scheduler">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF7ED' }}>
          <Send size={18} style={{ color: '#F5831F' }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Investor Pulse · Manual Trigger</h3>
          <p className="text-xs" style={{ color: '#666' }}>
            Weekly auto-send fires Monday 09:00 UTC to <span className="font-semibold">{recipientCount}</span> recipient{recipientCount === 1 ? '' : 's'}.
            Use the controls below to drop an ad-hoc send before a VC call.
          </p>
        </div>
        <a
          href="/api/admin/investor-pulse/preview"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-md"
          style={{ backgroundColor: '#F3F4F6', color: '#374151' }}
          data-testid="pulse-preview-btn"
        >
          <Eye size={12} /> Preview HTML
        </a>
      </div>

      {(msg || err) && (
        <div className="text-xs mb-3 px-3 py-2 rounded-md"
             style={{ backgroundColor: err ? '#FEE2E2' : '#D1FAE5', color: err ? '#991B1B' : '#065F46' }}
             data-testid="pulse-flash">
          {err || msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        {/* Send Test — safety valve: fires to your own inbox only */}
        <button
          onClick={sendTest}
          disabled={busy === 'test'}
          className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-lg disabled:opacity-50"
          style={{ backgroundColor: '#10B981', color: 'white' }}
          data-testid="pulse-send-test-btn"
          title="Sends only to your admin email (or one you specify). Skips dedup + archive."
        >
          {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          Send TEST to Me
        </button>

        {/* Send Now */}
        <button
          onClick={fireNow}
          disabled={busy === 'now'}
          className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
          style={{ backgroundColor: '#F5831F' }}
          data-testid="pulse-send-now-btn"
        >
          {busy === 'now' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Send to All Recipients Now
        </button>

        {/* Schedule */}
        <div className="flex flex-col gap-2 p-3 rounded-lg" style={{ backgroundColor: '#FAFAFA', border: '1px solid #EEE' }}>
          <div className="flex items-center gap-2">
            <Calendar size={14} style={{ color: '#666' }} />
            <span className="text-xs font-semibold" style={{ color: '#666' }}>Schedule for…</span>
            <button
              onClick={() => setScheduledFor(fridayCloseISO())}
              className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded"
              style={{ backgroundColor: '#FFF7ED', color: '#9A3412' }}
              data-testid="pulse-friday-close-btn"
              title="Friday 16:00 UTC — pre-market-close VC anchor"
            >
              Friday close
            </button>
          </div>
          <input
            type="datetime-local"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded-md"
            style={{ borderColor: '#E0E0E0' }}
            data-testid="pulse-schedule-datetime"
          />
          <input
            type="text"
            placeholder="Note (e.g. before BraveVC call)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded-md"
            style={{ borderColor: '#E0E0E0' }}
            data-testid="pulse-schedule-note"
          />
          <button
            onClick={queue}
            disabled={busy === 'queue'}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-md disabled:opacity-50"
            style={{ backgroundColor: '#1A1209' }}
            data-testid="pulse-queue-btn"
          >
            {busy === 'queue' ? <Loader2 size={12} className="animate-spin" /> : <Clock size={12} />}
            Queue ad-hoc pulse
          </button>
        </div>
      </div>

      {/* Queue / history */}
      {schedules.length > 0 && (
        <div className="pt-3 border-t" style={{ borderColor: '#F1F1F1' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#6B6257' }}>
            Recent ad-hoc sends
          </p>
          <div className="space-y-1.5" data-testid="pulse-schedule-list">
            {schedules.slice(0, 5).map((s) => {
              const st = STATUS_STYLE[s.status] || STATUS_STYLE.pending;
              const StIcon = st.Icon;
              return (
                <div
                  key={s.schedule_id}
                  className="flex items-center gap-2 px-3 py-2 rounded-md"
                  style={{ backgroundColor: '#FAFAFA', border: '1px solid #EEE' }}
                  data-testid={`pulse-schedule-row-${s.schedule_id}`}
                >
                  <StIcon size={13} style={{ color: st.text }} />
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: st.bg, color: st.text }}
                  >{st.label}</span>
                  <span className="text-xs font-mono" style={{ color: '#666' }}>{fmtSchedule(s.scheduled_for)}</span>
                  {s.note && <span className="text-xs truncate" style={{ color: '#666' }} title={s.note}>· {s.note}</span>}
                  {s.status === 'sent' && s.emails_sent !== undefined && (
                    <span className="text-[10px]" style={{ color: '#065F46' }}>· {s.emails_sent} sent</span>
                  )}
                  {s.status === 'pending' && (
                    <button
                      onClick={() => cancel(s.schedule_id)}
                      className="ml-auto text-gray-400 hover:text-red-600"
                      title="Cancel"
                      data-testid={`pulse-cancel-${s.schedule_id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[10px] italic mt-2" style={{ color: '#6B6257' }}>
            Worker polls every 30 min — a queued send fires on the next sweep after the target time.
          </p>
        </div>
      )}
    </div>
  );
};

export default InvestorPulseSchedulerCard;
